import React, { useState, useRef, useEffect } from "react";
import { Bell, XCircle, CheckCircle } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

// Données fictives (à connecter à votre API)
const notifications = [
  {
    id: 1,
    title: "Nouveau message",
    description: "Vous avez reçu un message de Thomas",
    time: "Il y a 5 min",
    read: false,
    type: "message",
  },
  {
    id: 2,
    title: "Projet validé",
    description: "Le projet 'Branding Braincrea' a été approuvé",
    time: "Il y a 2 heures",
    read: false,
    type: "success",
  },
  {
    id: 3,
    title: "Rappel",
    description: "Réunion avec l'équipe à 15h",
    time: "Hier",
    read: true,
    type: "reminder",
  },
];

const NotificationButton: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [items, setItems] = useState(notifications);
  const menuRef = useRef<HTMLDivElement>(null);
  const unreadCount = items.filter((n) => !n.read).length;

  // Fermeture au clic extérieur
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const markAsRead = (id: number) => {
    setItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, read: true } : item))
    );
  };

  const markAllAsRead = () => {
    setItems((prev) => prev.map((item) => ({ ...item, read: true })));
  };

  return (
    <div className="sm:relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative flex items-center justify-center w-9 h-9 rounded-lg bg-gradient-to-r from-primary/20 to-secondary/20 text-white shadow-[0_2px_8px_rgba(22,241,158,0.2)] hover:from-primary/30 hover:to-secondary/30 transition-all duration-200 border border-white/10 hover:scale-105"
      >
        <Bell size={16} />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 flex items-center justify-center w-4 h-4 text-[10px] font-bold bg-secondary rounded-full text-dark ring-2 ring-dark">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="absolute right-5 sm:right-0 mt-2 w-80 rounded-2xl bg-dark/95 backdrop-blur-md border border-white/10 shadow-2xl overflow-hidden z-50"
          >
            {/* En-tête */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 bg-gradient-to-r from-primary/5 to-secondary/5">
              <h3 className="text-sm font-semibold text-white">Notifications</h3>
              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="text-xs text-primary hover:text-secondary transition-colors"
                >
                  Tout marquer comme lu
                </button>
              )}
            </div>

            {/* Liste des notifications */}
            <div className="max-h-96 overflow-y-auto">
              {items.length === 0 ? (
                <div className="py-8 text-center text-gray-400 text-sm">
                  Aucune notification
                </div>
              ) : (
                items.map((notif) => (
                  <div
                    key={notif.id}
                    className={`relative px-4 py-3 border-b border-white/5 transition-colors hover:bg-white/5 cursor-pointer ${
                      !notif.read ? "bg-primary/5" : ""
                    }`}
                    onClick={() => markAsRead(notif.id)}
                  >
                    <div className="flex gap-3">
                      <div className="flex-shrink-0 mt-0.5">
                        {notif.type === "success" ? (
                          <CheckCircle size={16} className="text-primary" />
                        ) : (
                          <Bell size={16} className="text-secondary" />
                        )}
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-white">
                          {notif.title}
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          {notif.description}
                        </p>
                        <p className="text-[10px] text-gray-500 mt-1">
                          {notif.time}
                        </p>
                      </div>
                      {!notif.read && (
                        <div className="w-2 h-2 bg-secondary rounded-full mt-2" />
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Pied de page */}
            <div className="px-4 py-2 border-t border-white/10 text-center">
              <a
                href="#"
                className="text-xs text-primary hover:text-secondary transition-colors"
                onClick={() => setIsOpen(false)}
              >
                Voir toutes les notifications
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default NotificationButton;